import React, { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import {
  Button,
  ConfirmationDialog,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { revertNoaToReady, sendBillingCycleAgain } from '../../services/api';
import { PermissionSlug } from '../../constants/featureFlags';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { useToast } from '../../hooks/useToastContext';
import { useStore } from '../../store';

/**
 * Send a bill the payer never accepted back for correction (EMR-1242).
 *
 * One component for both managers, because the rule behind the action is one
 * rule: a refusal the biller can still correct is the biller's to send again.
 * `canResend` comes from the shared payer-response read model (EMR-1240): for a
 * claim it is true on every rejected cycle since EMR-1268, and for an NOA it is
 * true on every rejection, because a rejected NOA has no appeal and no cancel.
 * A denied claim is appealed rather than sent again, so the action never
 * renders on one — a denial carries its own cycle status.
 *
 * It confirms before a claim is resent when either the payer numbered the bill
 * or Roger has no payer evidence at all. Both cases can create a duplicate
 * original claim, so the confirmation stands wherever the action is mounted:
 * the compact row, which has no payer-response panel at all, and the drawer,
 * whose warnings explain the risk but do not block the click.
 */
export type SendAgainKind = 'claim' | 'noa';

const RESENDABLE_STATUS: Record<SendAgainKind, string> = {
  claim: 'REJECTED',
  noa: 'rejected',
};

export const SendAgainAction: React.FC<{
  kind: SendAgainKind;
  id: string;
  status: string;
  canResend: boolean;
  /** Whether the payer, rather than only Stedi, has responded about this claim. */
  hasPayerEvidence?: boolean;
  /** Set where the payer gave the bill a claim number. */
  hasPayerClaimControlNumber?: boolean;
  onDone: () => void | Promise<void>;
}> = ({
  kind,
  id,
  status,
  canResend,
  hasPayerEvidence = true,
  hasPayerClaimControlNumber = false,
  onDone,
}) => {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { error: showError } = useToast();
  const canSubmitClaims = useStore(state =>
    state.hasPermission(PermissionSlug.CLAIMS_SUBMIT)
  );

  if (
    !canResend ||
    status !== RESENDABLE_STATUS[kind] ||
    (kind === 'claim' && !canSubmitClaims)
  )
    return null;

  const hasNoPayerEvidence = kind === 'claim' && !hasPayerEvidence;
  const mustConfirm =
    hasNoPayerEvidence || (kind === 'claim' && hasPayerClaimControlNumber);

  const send = async () => {
    setConfirming(false);
    setBusy(true);
    try {
      const result =
        kind === 'claim'
          ? await sendBillingCycleAgain({ path: { billing_cycle_id: id } })
          : await revertNoaToReady({ path: { noa_id: id } });
      // The whole result, not just `result.error`: the status lives on
      // `result.response`, and without it getBusinessErrorMessage cannot tell a
      // 5xx body from a 4xx business message and would show the server's text.
      if (result.error) throw result;
    } catch (error) {
      // Never the raw server string: payer text and patient data both travel in
      // these responses.
      console.error('Failed to send this claim again:', error);
      showError(
        getBusinessErrorMessage(error) ?? 'Unable to send this claim again.'
      );
      setBusy(false);
      return;
    }

    // The send has already succeeded, so a refresh failure is its own message.
    // Telling the biller the send failed would invite them to send it twice.
    // Awaited so the button stays disabled until the row and counts settle.
    try {
      await onDone();
    } catch (error) {
      console.error('Failed to refresh after sending again:', error);
      showError('Sent again. Refresh the page to see the updated list.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              disabled={busy}
              onClick={() => (mustConfirm ? setConfirming(true) : send())}
              className='text-muted-foreground hover:text-foreground'
              aria-label='Send again'
            >
              <RotateCcw className='h-4 w-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='bottom' className='text-xs'>
            Send again
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <ConfirmationDialog
        open={confirming}
        title={
          hasNoPayerEvidence
            ? 'Roger has no payer response'
            : 'The payer already has this bill'
        }
        message={
          hasNoPayerEvidence
            ? 'Roger has no payer response for this bill. If it was submitted, sending again may create a duplicate.'
            : 'The payer gave this bill a claim number, so it has a record of it. Continue to create an editable resubmission from the rejected claim, then review and approve it before sending.'
        }
        confirmLabel='Continue'
        loading={busy}
        onAccept={send}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
};

export default SendAgainAction;
